import { CheckCircle2, Info } from "lucide-react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import type { CreateOrganizationResponse } from "../../types/clients-api.types";
import { KycLinkReveal } from "../kyc/KycLinkReveal";

/** KYC verification link issued right after the organization was created (verification engine). */
export interface IssuedKycLink {
  url: string;
  expiresAt: string | null;
}

export interface OrganizationSuccessStepProps {
  organization: CreateOrganizationResponse;
  /**
   * Corporate KYC link issued through the verification engine after create. Null when issuing failed — the
   * pending-verification note renders instead and the link can be generated later from the detail view.
   */
  kycLink?: IssuedKycLink | null;
}

/**
 * Final screen of the organization wizard — mirrors the mock's `org_success` state: confirmation banner plus
 * either the shareable KYC verification link or a note that verification was started automatically.
 */
export function OrganizationSuccessStep({ organization, kycLink }: OrganizationSuccessStepProps) {
  const { t } = useTranslations("clients");

  return (
    <div className="space-y-4">
      <ColocaAlert
        variant="success"
        icon={CheckCircle2}
        text={t("org_success_text", { name: organization.name })}
      />
      {kycLink ? (
        <KycLinkReveal link={kycLink.url} expiresAt={kycLink.expiresAt} />
      ) : (
        <ColocaAlert variant="secondary" icon={Info} text={t("org_success_kyc_pending")} />
      )}
    </div>
  );
}
